/**
 * Users API Client
 *
 * API client functions for all User-related endpoints.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 */

import type {
  AccountDeletionCancelResponse,
  AccountDeletionConfirmResponse,
  AccountDeletionRequestResponse,
  AccountDeletionStatusResponse,
} from '@/types/accountDeletion.types';
import type { DataExportRequestResponse, DataExportStatusResponse } from '@/types/dataExport.types';

import { BaseApiClient } from './baseApiClient';

import type { User, Organization } from '@gw/ui/types';

// GW-1709/GW-2324: Types for user profile update
interface UserProfileUpdate {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  companyName?: string;
  workState?: string;
}

interface UserProfileResponse {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phoneNumber?: string;
  companyName?: string;
  token_balance: number;
  created_at: string;
  updated_at: string;
}

// GW-2326: Minimal user summary for entity name resolution
interface UserSummary {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

class UsersApiClient extends BaseApiClient {
  constructor() {
    // suppressSessionExpired: the users API is called in the post-login
    // window (before org context is established) and during onboarding flows.
    // A 401 here means the session token hasn't propagated to the API yet —
    // not that the session has truly expired. Suppressing prevents a spurious
    // logout immediately after sign-in (GW-6672).
    super({ suppressSessionExpired: true });
  }

  // ============================================================================
  // User Management APIs
  // ============================================================================

  /**
   * Get Current User
   * GET /users/me
   * Authorization: Any authenticated user
   *
   * Retrieves the current authenticated user's profile information.
   *
   * @returns Promise<User> - Current user's profile data
   * @throws {ApiError} When API request fails or user is not authenticated
   */
  async getCurrentUser(): Promise<User> {
    return this.get<User>('/users/me');
  }

  /**
   * Get User Organizations
   * GET /users/me/organizations
   * Authorization: Any authenticated user
   *
   * Retrieves all organizations that the current user belongs to.
   * Includes the user's roles for each organization.
   *
   * @returns Promise<Organization[]> - Array of organizations the user belongs to
   * @throws {ApiError} When API request fails
   */
  async getUserOrganizations(): Promise<Organization[]> {
    const response = await this.get<{ organizations: Organization[] }>('/users/me/organizations');
    return response.organizations;
  }

  /**
   * Select Organization
   * POST /users/me/select-organization
   * Authorization: Any authenticated user
   *
   * Switches the user's current organization context. The backend updates the
   * Cognito custom:orgId attribute server-side and signals `refreshToken: true`
   * so the client knows to force-refresh its own session against Cognito to pick
   * up the new claim (see auth.refreshSession() in src/lib/awsCognito.util.ts).
   * The backend never returns a `token` field — the new JWT is minted
   * client-side, not handed back by this endpoint (GW-9136/GW-9137).
   *
   * @param orgId - ID of the organization to select
   * @returns Promise<SelectOrganizationResponse> - refreshToken signal + organization info
   * @throws {ApiError} When organization not found or user is not a member
   */
  async selectOrganization(orgId: string): Promise<{
    message: string;
    orgId: string;
    roles: string[];
    refreshToken: boolean;
  }> {
    return this.post<{
      message: string;
      orgId: string;
      roles: string[];
      refreshToken: boolean;
    }>('/users/me/select-organization', { organizationId: orgId });
  }

  // ============================================================================
  // GW-2326: User Lookup for Entity Name Resolution
  // ============================================================================

  /**
   * Get User by ID
   * GET /users/{userId}
   * Authorization: global_admin (used by useEntityNames for trust score display)
   *
   * Fetches a user's profile for display name resolution. Returns first_name,
   * last_name, and email so the caller can build a display name.
   *
   * @param userId - ID of the user to fetch
   * @returns Promise<UserSummary> - User summary with name and email fields
   * @throws {ApiError} When user not found (404) or access denied (403)
   */
  async getUser(userId: string): Promise<UserSummary> {
    return this.get<UserSummary>(`/users/${encodeURIComponent(userId)}`);
  }

  // ============================================================================
  // GW-1709/GW-2324: User Profile Update
  // ============================================================================

  /**
   * Update User Profile
   * PUT /users/{userId}
   * Authorization: Any authenticated user (can only update own profile)
   *
   * Updates the user's profile fields including first name, last name,
   * phone number, and company name.
   *
   * @param userId - ID of the user to update
   * @param data - Profile update data (firstName, lastName, phoneNumber?, companyName?)
   * @returns Promise<UserProfileResponse> - Updated profile data
   * @throws {ApiError} When API request fails or user is not authorized
   */
  async updateUserProfile(userId: string, data: UserProfileUpdate): Promise<UserProfileResponse> {
    return this.put<UserProfileResponse>(`/users/${encodeURIComponent(userId)}`, data);
  }

  // ============================================================================
  // Data Export APIs (GDPR Article 20 - Right to Data Portability)
  // ============================================================================

  /**
   * Request Data Export
   * POST /users/me/export-data
   * Authorization: Any authenticated user
   *
   * Requests a GDPR data export. Rate limited to one request per 24 hours.
   * The export is processed asynchronously and the user will receive an email
   * when the export is ready for download.
   *
   * @returns Promise<DataExportRequestResponse> - Export request details
   * @throws {ApiError} When rate limited (429) or API request fails
   */
  async requestDataExport(): Promise<DataExportRequestResponse> {
    return this.post<DataExportRequestResponse>('/users/me/export-data');
  }

  /**
   * Get Export Status
   * GET /users/me/export-data/{request_id}
   * Authorization: Any authenticated user (can only access own exports)
   *
   * Retrieves the status of a data export request. Returns download URL
   * when the export is completed.
   *
   * @param requestId - ID of the export request to check
   * @returns Promise<DataExportStatusResponse> - Export status details
   * @throws {ApiError} When export not found (404) or access denied (403)
   */
  async getExportStatus(requestId: string): Promise<DataExportStatusResponse> {
    return this.get<DataExportStatusResponse>(
      `/users/me/export-data/${encodeURIComponent(requestId)}`
    );
  }

  // ============================================================================
  // GW-141/GW-8266: Account Deletion APIs (GDPR Right to Erasure)
  // ============================================================================

  /**
   * Get Account Deletion Status
   * GET /users/me/delete-account
   * Authorization: Any authenticated user
   *
   * Checks whether there is a pending or active account deletion request for
   * the current user.
   *
   * @returns Promise<AccountDeletionStatusResponse> - Current deletion request status
   * @throws {ApiError} When API request fails
   */
  async getAccountDeletionStatus(): Promise<AccountDeletionStatusResponse> {
    return this.get<AccountDeletionStatusResponse>('/users/me/delete-account');
  }

  /**
   * Request Account Deletion
   * POST /users/me/delete-account
   * Authorization: Any authenticated user
   *
   * Initiates a GDPR Right to Erasure request. The request must be confirmed
   * via {@link confirmAccountDeletion} before the 30-day grace period starts.
   *
   * @returns Promise<AccountDeletionRequestResponse> - Deletion request details
   * @throws {ApiError} When an active request already exists (409) or API request fails
   */
  async requestAccountDeletion(): Promise<AccountDeletionRequestResponse> {
    return this.post<AccountDeletionRequestResponse>('/users/me/delete-account');
  }

  /**
   * Confirm Account Deletion
   * POST /users/me/delete-account/confirm
   * Authorization: Any authenticated user
   *
   * Confirms a pending deletion request and starts the 30-day grace period.
   *
   * @returns Promise<AccountDeletionConfirmResponse> - Confirmed request with grace period end
   * @throws {ApiError} When no pending request exists (404) or API request fails
   */
  async confirmAccountDeletion(): Promise<AccountDeletionConfirmResponse> {
    return this.post<AccountDeletionConfirmResponse>('/users/me/delete-account/confirm');
  }

  /**
   * Cancel Account Deletion
   * DELETE /users/me/delete-account
   * Authorization: Any authenticated user
   *
   * Cancels a pending or confirmed deletion request while still within the
   * grace period.
   *
   * @returns Promise<AccountDeletionCancelResponse> - Cancellation confirmation
   * @throws {ApiError} When no active request exists (404) or API request fails
   */
  async cancelAccountDeletion(): Promise<AccountDeletionCancelResponse> {
    return this.delete<AccountDeletionCancelResponse>('/users/me/delete-account');
  }
}

// Export singleton instance
export const usersApi = new UsersApiClient();
